import {
  Container,
  Col,
  Button,
  Image,
  Row,
  FloatingLabel,
  Form,
  OverlayTrigger,
  Popover,
} from 'react-bootstrap';
import classes from './Playground.module.css';
import React, { ChangeEvent, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import Link from '../../components/Link';
import {
  ArtData,
  EncodedImage,
  PART_TYPES,
  PartType,
  getAlpData,
  getRandomAlpSeed,
} from '@nouns/assets';
import { buildSVG, PNGCollectionEncoder } from '@nouns/sdk';
import InfoIcon from '../../assets/icons/Info.svg';
import Alp from '../../components/Alp';
import AlpModal from './AlpModal';
import { PNG } from 'pngjs';
import { Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { partName, useAlpArt } from '../../utils/alpArt';

interface Trait {
  title: string;
  traitNames: string[];
}

interface PendingCustomTrait {
  type: PartType;
  data: string;
  filename: string;
}

const alpsProtocolLink = (
  <Link
    text={<Trans>Alps Protocol</Trans>}
    url="https://github.com/AlpsDAO/alps-dao-monorepo/"
    leavesPage={true}
  />
);

const nounsAssetsLink = (
  <Link
    text="nouns-assets"
    url="https://github.com/nounsDAO/nouns-monorepo/tree/master/packages/nouns-assets"
    leavesPage={true}
  />
);

const alpsAssetsLink = (
  <Link
    text="alps-assets"
    url="https://github.com/AlpsDAO/alps-dao-monorepo/tree/master/packages/nouns-assets"
    leavesPage={true}
  />
);

const gnarsAssetsLink = <Link text="gnars-assets" url="https://gnars.wtf/" leavesPage={true} />;

const alpsSDKLink = (
  <Link
    text="alps-sdk"
    url="https://github.com/AlpsDAO/alps-dao-monorepo/tree/master/packages/nouns-sdk"
    leavesPage={true}
  />
);

const DEFAULT_TRAIT_TYPE: PartType = 'heads';

const BRICKS_URL = 'https://bricks.alps.wtf';
// Seed keys and the image lists they index, in the order the brick builder's ?seed= link takes them
const SEED_PARTS: [string, PartType | null][] = [
  ['background', null],
  ['body', 'bodies'],
  ['accessory', 'accessories'],
  ['head', 'heads'],
  ['glasses', 'glasses'],
];

interface PlaygroundAlp {
  svg: string;
  // the brick builder's link for this Alp, or undefined if it wears a custom trait (the builder only
  // knows the standard ones)
  bricksUrl?: string;
}

const traitKeyToTitle: Record<PartType, string> = {
  heads: 'head',
  glasses: 'glasses',
  bodies: 'body',
  accessories: 'accessory',
};

const capitalizeFirstLetter = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

const traitKeyToLocalizedTraitKeyFirstLetterCapitalized = (s: string): ReactNode => {
  const traitMap = new Map([
    ['background', <Trans>Background</Trans>],
    ['body', <Trans>Body</Trans>],
    ['accessory', <Trans>Accessory</Trans>],
    ['head', <Trans>Head</Trans>],
    ['glasses', <Trans>Glasses</Trans>],
  ]);

  return traitMap.get(s);
};

const Playground: React.FC = () => {
  const { art } = useAlpArt();
  const [alpSvgs, setAlpSvgs] = useState<PlaygroundAlp[]>();
  // uploaded traits go to the front of their list, shifting the standard ones along
  const [customTraits, setCustomTraits] = useState<Partial<Record<PartType, EncodedImage[]>>>({});
  const [modSeed, setModSeed] = useState<{ [key: string]: number }>();
  const [initLoad, setInitLoad] = useState<boolean>(true);
  const [displayAlp, setDisplayAlp] = useState<boolean>(false);
  const [indexOfAlpToDisplay, setIndexOfAlpToDisplay] = useState<number>();
  const [selectIndexes, setSelectIndexes] = useState<Record<string, number>>({});
  const [pendingTrait, setPendingTrait] = useState<PendingCustomTrait>();
  const [isPendingTraitValid, setPendingTraitValid] = useState<boolean>();

  const customTraitFileRef = useRef<HTMLInputElement>(null);

  // Uploaded traits add any new colours to the encoder's palette
  const encoder = useMemo(() => new PNGCollectionEncoder(art.palette), [art.palette]);
  const playgroundArt: ArtData = useMemo(
    () => ({
      ...art,
      images: Object.fromEntries(
        PART_TYPES.map(type => [type, [...(customTraits[type] ?? []), ...art.images[type]]]),
      ) as ArtData['images'],
    }),
    [art, customTraits],
  );
  const traits: Trait[] = useMemo(
    () => [
      {
        title: 'background',
        traitNames: playgroundArt.bgcolors.map(
          (colour, i) => playgroundArt.bgnames[i] || `#${colour}`,
        ),
      },
      ...PART_TYPES.map(type => ({
        title: traitKeyToTitle[type],
        traitNames: playgroundArt.images[type].map(part => partName(part)),
      })),
    ],
    [playgroundArt],
  );

  const generateAlpSvg = React.useCallback(
    (amount: number = 1) => {
      for (let i = 0; i < amount; i++) {
        const seed = { ...getRandomAlpSeed(playgroundArt), ...modSeed };
        const { parts, background } = getAlpData(seed, playgroundArt);
        const svg = buildSVG(parts, encoder.data.palette, background);
        const indexes = seed as Record<string, number>;
        const standard = SEED_PARTS.map(
          ([key, list]) => indexes[key] - (list ? customTraits[list]?.length ?? 0 : 0),
        );
        const alp = {
          svg,
          bricksUrl: standard.every(i => i >= 0) ? `${BRICKS_URL}/?seed=${standard.join('-')}` : undefined,
        };
        setAlpSvgs(prev => {
          return prev ? [alp, ...prev] : [alp];
        });
      }
    },
    [playgroundArt, encoder, modSeed, customTraits],
  );

  useEffect(() => {
    if (initLoad) {
      generateAlpSvg(8);
      setInitLoad(false);
    }
  }, [generateAlpSvg, initLoad]);

  // Option values are the trait's index, with -1 for random
  const traitOptions = (trait: Trait) => {
    return Array.from(Array(trait.traitNames.length + 1)).map((_, index) => (
      <option key={index} value={index - 1}>
        {index === 0 ? `Random` : trait.traitNames[index - 1]}
      </option>
    ));
  };

  const traitButtonHandler = (trait: Trait, traitIndex: number) => {
    setModSeed(prev => {
      // -1 traitIndex = random
      if (traitIndex < 0) {
        let state = { ...prev };
        delete state[trait.title];
        return state;
      }
      return {
        ...prev,
        [trait.title]: traitIndex,
      };
    });
  };

  const resetTraitFileUpload = () => {
    if (customTraitFileRef.current) {
      customTraitFileRef.current.value = '';
    }
  };

  let pendingTraitErrorTimeout: NodeJS.Timeout;
  const setPendingTraitInvalid = () => {
    setPendingTraitValid(false);
    resetTraitFileUpload();
    pendingTraitErrorTimeout = setTimeout(() => {
      setPendingTraitValid(undefined);
    }, 5_000);
  };

  const validateAndSetCustomTrait = (file: File | undefined) => {
    if (pendingTraitErrorTimeout) {
      clearTimeout(pendingTraitErrorTimeout);
    }
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = e => {
      try {
        const buffer = Buffer.from(e?.target?.result!);
        const png = PNG.sync.read(buffer);
        if (png.width !== 32 || png.height !== 32) {
          throw new Error('Image must be 32x32');
        }
        const filename = file.name?.replace('.png', '') || 'custom';
        const data = encoder.encodeImage(filename, {
          width: png.width,
          height: png.height,
          rgbaAt: (x: number, y: number) => {
            const idx = (png.width * y + x) << 2;
            const [r, g, b, a] = [
              png.data[idx],
              png.data[idx + 1],
              png.data[idx + 2],
              png.data[idx + 3],
            ];
            return {
              r,
              g,
              b,
              a,
            };
          },
        });
        setPendingTrait({
          data,
          filename,
          type: DEFAULT_TRAIT_TYPE,
        });
        setPendingTraitValid(true);
      } catch (error) {
        setPendingTraitInvalid();
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const uploadCustomTrait = () => {
    const { type, data, filename } = pendingTrait || {};
    if (type && data && filename) {
      setCustomTraits(prev => ({
        ...prev,
        [type]: [{ filename, name: filename, data }, ...(prev[type] ?? [])],
      }));
      const title = traitKeyToTitle[type];
      const trait = traits.find(t => t.title === title);

      resetTraitFileUpload();
      setPendingTrait(undefined);
      setPendingTraitValid(undefined);
      traitButtonHandler(trait!, 0);
      setSelectIndexes({
        ...selectIndexes,
        [title]: 0,
      });
    }
  };

  return (
    <>
      {displayAlp && indexOfAlpToDisplay !== undefined && alpSvgs && (
        <AlpModal
          onDismiss={() => {
            setDisplayAlp(false);
          }}
          svg={alpSvgs[indexOfAlpToDisplay].svg}
          bricksUrl={alpSvgs[indexOfAlpToDisplay].bricksUrl}
        />
      )}

      <Container fluid="lg">
        <Row>
          <Col lg={10} className={classes.headerRow}>
            <span>
              <Trans>Explore</Trans>
            </span>
            <h1>
              <Trans>Playground</Trans>
            </h1>
            <p>
              <Trans>
                The playground is built with the {alpsProtocolLink}. An Alp’s traits come from its
                seed, which draws on {alpsAssetsLink}, {gnarsAssetsLink} and {nounsAssetsLink}, and
                is rendered with the {alpsSDKLink}.
              </Trans>
            </p>
            <p>
              <Trans>
                Tap any Alp to download it, or to turn it into a brick bust you can really build with{' '}
                <a href={BRICKS_URL} target="_blank" rel="noreferrer">
                  Alps to Bricks
                </a>
                .
              </Trans>
            </p>
          </Col>
        </Row>
        <Row>
          <Col lg={3}>
            <Col lg={12}>
              <Button
                onClick={() => {
                  generateAlpSvg();
                }}
                className={classes.primaryBtn}
              >
                <Trans>Generate Alps</Trans>
              </Button>
            </Col>
            <Row>
              {traits.map((trait, index) => {
                return (
                  <Col lg={12} xs={6}>
                    <Form className={classes.traitForm}>
                      <FloatingLabel
                        controlId="floatingSelect"
                        label={traitKeyToLocalizedTraitKeyFirstLetterCapitalized(trait.title)}
                        key={index}
                        className={classes.floatingLabel}
                      >
                        <Form.Select
                          aria-label="Floating label select example"
                          className={classes.traitFormBtn}
                          value={selectIndexes?.[trait.title] ?? -1}
                          onChange={e => {
                            let index = e.currentTarget.selectedIndex;
                            traitButtonHandler(trait, index - 1); // - 1 to account for 'random'
                            setSelectIndexes({
                              ...selectIndexes,
                              [trait.title]: index - 1,
                            });
                          }}
                        >
                          {traitOptions(trait)}
                        </Form.Select>
                      </FloatingLabel>
                    </Form>
                  </Col>
                );
              })}
            </Row>
            <label style={{ margin: '1rem 0 .25rem 0' }} htmlFor="custom-trait-upload">
              <Trans>Upload Custom Trait</Trans>
              <OverlayTrigger
                trigger="hover"
                placement="top"
                overlay={
                  <Popover>
                    <div style={{ padding: '0.25rem' }}>
                      <Trans>Only 32x32 PNG images are accepted</Trans>
                    </div>
                  </Popover>
                }
              >
                <Image
                  style={{ margin: '0 0 .25rem .25rem' }}
                  src={InfoIcon}
                  className={classes.voteIcon}
                />
              </OverlayTrigger>
            </label>
            <Form.Control
              type="file"
              id="custom-trait-upload"
              accept="image/PNG"
              isValid={isPendingTraitValid}
              isInvalid={isPendingTraitValid === false}
              ref={customTraitFileRef}
              className={classes.fileUpload}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                validateAndSetCustomTrait(e.target.files?.[0])
              }
            />
            {pendingTrait && (
              <>
                <FloatingLabel label="Custom Trait Type" className={classes.floatingLabel}>
                  <Form.Select
                    aria-label="Custom Trait Type"
                    className={classes.traitFormBtn}
                    onChange={e =>
                      setPendingTrait({ ...pendingTrait, type: e.target.value as PartType })
                    }
                  >
                    {Object.entries(traitKeyToTitle).map(([key, title]) => (
                      <option value={key}>{capitalizeFirstLetter(title)}</option>
                    ))}
                  </Form.Select>
                </FloatingLabel>
                <Button onClick={() => uploadCustomTrait()} className={classes.primaryBtn}>
                  <Trans>Upload</Trans>
                </Button>
              </>
            )}
            <p className={classes.alpYearsFooter}>
              <Trans>
                You've generated{' '}
                {i18n.number(parseInt(alpSvgs ? (alpSvgs.length / 365).toFixed(2) : '0'))} years
                worth of Alps
              </Trans>
            </p>
          </Col>
          <Col lg={9}>
            <Row>
              {alpSvgs &&
                alpSvgs.map(({ svg }, i) => {
                  return (
                    <Col xs={4} lg={3} key={i}>
                      <div
                        onClick={() => {
                          setIndexOfAlpToDisplay(i);
                          setDisplayAlp(true);
                        }}
                      >
                        <Alp
                          imgPath={`data:image/svg+xml;base64,${btoa(svg)}`}
                          alt="alp"
                          className={classes.alpImg}
                          wrapperClassName={classes.alpWrapper}
                        />
                      </div>
                    </Col>
                  );
                })}
            </Row>
          </Col>
        </Row>
      </Container>
    </>
  );
};
export default Playground;
